import * as SunCalc from 'suncalc';
import { TimeStep } from '../types';
import {
  getMoonPhaseDetails,
  isFullMoon,
  isNewMoon,
  isSupermoon,
  isHarvestMoon,
  isMoonlessNight,
  calculateLongTermDarkSkiesScore,
  getUtcDateForLocalTime
} from '../utils/astronomy';

export interface CalendarDaySummary {
  date: Date;
  dateStr: string; // YYYY-MM-DD
  dayNumber: number;
  isToday: boolean;
  isSelected: boolean;
  moonIcon: string;
  moonName: string;
  moonIllumPct: number;
  isFullMoon: boolean;
  isNewMoon: boolean;
  isSupermoon: boolean;
  isHarvestMoon: boolean;
  isMoonlessNight: boolean;
  darkSkiesScore: number;
  darkSkiesLabel: string;
  sunriseTime: string;
  sunsetTime: string;
  moonriseTime: string;
  moonsetTime: string;
}

export { getUtcDateForLocalTime } from '../utils/astronomy';

export class SolarLunarService {
  /**
   * Generates a 24-hour synthetic timeline step range for a given date in 5-minute increments normalized to local timezone.
   * This allows the timeline slider to scrub through Midnight to 11:55PM in 5-minute increments.
   */
  public static generateDayTimeline(targetDate: Date, tz: string = 'America/Los_Angeles'): TimeStep[] {
    const timeSteps: TimeStep[] = [];

    const dFmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour12: false
    });
    const parts = dFmt.formatToParts(targetDate);
    const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    const localYear = getPart('year');
    const localMonth = getPart('month') - 1;
    const localDay = getPart('day');

    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 5) {
        const utcDate = getUtcDateForLocalTime(localYear, localMonth, localDay, h, m, tz);

        const yrStr = utcDate.getUTCFullYear();
        const moStr = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
        const dyStr = String(utcDate.getUTCDate()).padStart(2, '0');
        const dateStr = `${yrStr}-${moStr}-${dyStr}`;

        const hrStr = String(utcDate.getUTCHours()).padStart(2, '0');
        const minStr = String(utcDate.getUTCMinutes()).padStart(2, '0');
        const hourStr = `${hrStr}:${minStr}`;

        timeSteps.push({
          date: dateStr,
          hour: hourStr,
          raw: utcDate.toISOString()
        });
      }
    }

    return timeSteps;
  }


  /**
   * Returns calendar grid data for a specific year and month (0-indexed).
   * Includes key event flags and long-term dark skies rating.
   */
  public static getMonthCalendar(
    year: number,
    month: number,
    lat: number,
    lon: number,
    bortle: number = 3,
    selectedDateStr?: string,
    tz: string = 'America/Los_Angeles'
  ): CalendarDaySummary[] {
    const days: CalendarDaySummary[] = [];
    const todayStr = new Date().toISOString().split('T')[0];

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d, 12, 0, 0); // Noon reference
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

      const moonDetails = getMoonPhaseDetails(date);
      const moonIllum = SunCalc.getMoonIllumination(date);
      const moonIllumPct = Math.round(moonIllum.fraction * 100);

      const fullM = isFullMoon(date);
      const newM = isNewMoon(date);
      const superM = isSupermoon(date, lat, lon);
      const harvestM = isHarvestMoon(date);
      const moonless = isMoonlessNight(date, lat, lon);

      const darkSkies = calculateLongTermDarkSkiesScore(bortle, date, lat, lon);

      const sTimes = SunCalc.getTimes(date, lat, lon);
      const mTimes = SunCalc.getMoonTimes(date, lat, lon);

      const fmtTime = (t?: Date | null) => {
        if (!t || isNaN(t.getTime())) return '--';
        return t.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
      };

      days.push({
        date,
        dateStr,
        dayNumber: d,
        isToday: dateStr === todayStr,
        isSelected: dateStr === selectedDateStr,
        moonIcon: moonDetails.icon,
        moonName: moonDetails.name,
        moonIllumPct,
        isFullMoon: fullM,
        isNewMoon: newM,
        isSupermoon: superM,
        isHarvestMoon: harvestM,
        isMoonlessNight: moonless,
        darkSkiesScore: darkSkies.score,
        darkSkiesLabel: darkSkies.label,
        sunriseTime: fmtTime(sTimes.sunrise),
        sunsetTime: fmtTime(sTimes.sunset),
        moonriseTime: fmtTime(mTimes.rise),
        moonsetTime: fmtTime(mTimes.set)
      });
    }

    return days;
  }
}
